import { normalizeSceneAudioHistory } from './audioHistory'

/**
 * Formats the output from the duration calculation API into a structured shotList
 */
export const formatDurationOutput = (output, existingScenes) => {
  return Object.entries(output).map(([sceneKey, sceneValue], index) => {
    const sceneIndex = parseInt(sceneKey.replace('scene ', '')) - 1
    const existingScene = existingScenes[sceneIndex] || {}

    const { scene_location = '', shots = [] } = sceneValue

    const formattedShots = shots.map((shotObj, shotIndex) => {
      // Handle both flat { title, video_duration, key, ... }
      // and wrapped { "shot N": { ... } } formats
      let shotKey, shotValue
      const firstKey = Object.keys(shotObj)[0]
      const isWrapped =
        firstKey &&
        firstKey.startsWith('shot ') &&
        typeof shotObj[firstKey] === 'object'

      if (isWrapped) {
        shotKey = firstKey
        shotValue = shotObj[firstKey]
      } else {
        // Flat format — the object itself is the shot data
        shotKey = shotObj.key || `shot ${shotIndex + 1}`
        shotValue = shotObj
      }

      // Match existing shot by key, then fall back to index
      const existingShot =
        (existingScene.shots || []).find(s => {
          const sKey = (s.key || '').toString().trim()
          return sKey === (shotKey || '').toString().trim()
        }) ||
        (existingScene.shots || [])[shotIndex] ||
        {}

      const resolvedId = existingShot.id || `${sceneKey}-shot-${shotIndex + 1}`

      const resolvedDuration =
        shotValue.video_duration != null && shotValue.video_duration !== ''
          ? shotValue.video_duration
          : existingShot.video_duration != null &&
            existingShot.video_duration !== ''
          ? existingShot.video_duration
          : ''

      return {
        ...existingShot,
        id: resolvedId,
        key: shotKey,
        title: shotValue.title || existingShot.title || '',
        initial_image_prompt:
          shotValue['initial image prompt'] ||
          shotValue.initial_image_prompt ||
          existingShot.initial_image_prompt ||
          '',
        scenery: shotValue.scenery || existingShot.scenery || '',
        shot_direction:
          shotValue['shot direction'] ||
          shotValue.shot_direction ||
          existingShot.shot_direction ||
          '',
        video_prompt:
          shotValue['video prompt'] ||
          shotValue.video_prompt ||
          existingShot.video_prompt ||
          '',
        voiceover_text:
          shotValue['voiceover text'] ||
          shotValue.voiceover_text ||
          existingShot.voiceover_text ||
          '',
        lighting: shotValue.lighting || existingShot.lighting || '',
        dialogue: shotValue.dialogue || existingShot.dialogue || {},
        video_duration: resolvedDuration
      }
    })

    return normalizeSceneAudioHistory({
      ...existingScene,
      id: existingScene.id || sceneKey,
      title: existingScene.title || existingScene.id || sceneKey,
      description: scene_location || existingScene.description,
      shots: formattedShots,
      aggregated_voiceover:
        sceneValue.aggregated_voiceover ||
        existingScene.aggregated_voiceover ||
        '',
      audio_duration:
        sceneValue.audio_duration != null
          ? sceneValue.audio_duration
          : existingScene.audio_duration != null
          ? existingScene.audio_duration
          : 0,
      audio_duration_estimated:
        sceneValue.audio_duration_estimated !== undefined
          ? sceneValue.audio_duration_estimated
          : existingScene.audio_duration_estimated,
      audio_link: sceneValue.audio_link || existingScene.audio_link || '',
      audio_history:
        sceneValue.audio_history || existingScene.audio_history || [],
      scene_location: scene_location || existingScene.scene_location
    })
  })
}

// Helper to normalize input data into scenes
const normalizeScenes = targetData => {
  const isFlatClips =
    Array.isArray(targetData) &&
    targetData.length > 0 &&
    !targetData[0].shots &&
    targetData[0].sceneId

  if (isFlatClips) {
    const scenesMap = new Map()
    targetData.forEach(clip => {
      if (!scenesMap.has(clip.sceneId)) {
        scenesMap.set(clip.sceneId, {
          id: clip.sceneId,
          title: clip.sceneTitle || clip.sceneId,
          location: clip.location || '',
          voiceover_text: clip.aggregated_voiceover || '',
          audio_link: clip.audio_link || '',
          audio_duration: clip.audio_duration || 0,
          audio_history: clip.audio_history || [],
          shots: []
        })
      }
      scenesMap.get(clip.sceneId).shots.push(clip)
    })
    return Array.from(scenesMap.values()).map(normalizeSceneAudioHistory)
  }
  return (targetData || []).map(normalizeSceneAudioHistory)
}

// Helper to build the clustered shooting list and capture flat shot info
const buildClusteredShootingList = (scenes, locationMap) => {
  const clustered_shooting_list = {}
  const flatShots = []
  const shot_volume_settings = {}
  let globalShotCounter = 0

  scenes.forEach((scene, index) => {
    const sceneNumber = index + 1
    const sceneKey = `scene ${sceneNumber}`

    const mappedShots = scene.shots.map((shot, shotIndex) => {
      globalShotCounter++
      const shotNumber = globalShotCounter
      const shotIndexInScene = shotIndex + 1 // 1-indexed within scene
      const shotKey = shot.key || shot.id || `shot ${shotIndex + 1}`

      // Build volume settings using the same global counter
      const volume =
        shot.volume !== undefined && shot.volume !== null
          ? parseInt(shot.volume)
          : 25
      shot_volume_settings[shotNumber] = volume

      const formattedShot = {
        ...shot,
        title: shot.title || shot.shotTitle || `Shot ${shotIndex + 1}`,
        shot_direction: shot.shot_direction || '',
        dialogue: shot.dialogue || {},
        voiceover_text: shot.voiceover_text || '',
        lighting: shot.lighting || '',
        location: shot.location || '',
        initial_image_prompt: shot.initial_image_prompt || '',
        video_prompt: shot.video_prompt || '',
        scenery: shot.scenery || '',
        video_duration:
          shot.video_duration ||
          shot.videoDuration ||
          shot.generated_video_duration ||
          '',
        shot_number: shotNumber, // Use global counter
        generated_image_url: shot.imageSrc || '',
        video_url: shot.vidSrc || '',
        volume: shot.volume,
        audio_url: scene.audio_link || null
      }

      // Store metadata for transition building
      flatShots.push({
        shotNumber,
        shotIndexInScene, // 1-indexed shot index within this scene
        totalShotsInScene: scene.shots.length,
        sceneNumber,
        sceneIndex: index,
        originalSceneId: scene.id,
        originalShotId: shot.id || shot.shotId || shot.key,
        isFirstInScene: shotIndex === 0,
        isLastInScene: shotIndex === scene.shots.length - 1,
        transition: shot.transition // Capture specific transition data if present
      })

      return { [shotKey]: formattedShot }
    })

    clustered_shooting_list[sceneKey] = {
      ...scene,
      scene_location:
        scene.location || scene.scene_location || `__location${index + 1}.1__`,
      aggregated_voiceover:
        scene.voiceover_text || scene.aggregated_voiceover || '',
      audio_link: scene.audio_link || '',
      audio_duration: scene.audio_duration || 0,
      audio_duration_estimated: scene.audio_duration_estimated || false,
      shots: mappedShots
    }
  })

  if (locationMap) {
    clustered_shooting_list.location_map = locationMap
  }

  return { clustered_shooting_list, flatShots, shot_volume_settings }
}

// Helper to build voiceover paths
const buildVoiceoverPaths = scenes => {
  const voiceover_paths = {}
  scenes.forEach((scene, index) => {
    if (scene.audio_link) {
      voiceover_paths[`scene ${index + 1}`] = scene.audio_link
    }
  })
  return voiceover_paths
}

// Helper to build trimmed video paths
const buildTrimmedVideoPaths = scenes => {
  const trimmed_video_paths = {}
  scenes.forEach((scene, index) => {
    const sceneShots = {}
    scene.shots.forEach((shot, shotIndex) => {
      const shotKey = shot.key || `shot ${shotIndex + 1}`
      if (shot.vidSrc) {
        sceneShots[shotKey] = shot.vidSrc
      }
    })
    if (Object.keys(sceneShots).length > 0) {
      trimmed_video_paths[`scene ${index + 1}`] = sceneShots
    }
  })
  return trimmed_video_paths
}

// Helper to extract transition data from transitionsMap
// Handles both old string format and new object format { transition, duration }
const getTransitionData = (transitionsMap, key, defaultDuration = 1.0) => {
  const value = transitionsMap[key]
  if (!value) return null

  // Old format: just a string
  if (typeof value === 'string') {
    return { transition: value, duration: defaultDuration }
  }

  // New format: { transition, duration }
  return {
    transition: value.transition,
    duration: value.duration || defaultDuration
  }
}

// Helper to build transitions
// from/to use shot index WITHIN the scene (1-indexed), not global shot numbers
const buildTransitions = (flatShots, transitionsMap = {}) => {
  const transitions = []

  if (flatShots.length === 0) return transitions

  // Duration constraints
  const MIN_DURATION = 0.5
  const MAX_DURATION = 3.0
  const DEFAULT_DURATION = 1.0

  // 1. Initial transition (null -> first shot of first scene)
  // Key: `${firstScene.id}-start`
  const firstShot = flatShots[0]
  const startKey = `${firstShot.originalSceneId}-start`
  const startTransition = getTransitionData(
    transitionsMap,
    startKey,
    DEFAULT_DURATION
  )

  if (startTransition) {
    transitions.push({
      transition_type: 'between_shots',
      from: null,
      to: 1, // First shot in the scene
      transition_nature: startTransition.transition,
      scene_number: firstShot.sceneNumber,
      duration: Math.min(
        MAX_DURATION,
        Math.max(MIN_DURATION, startTransition.duration)
      )
    })
  }

  // 2. Iterate through shots to build transitions
  for (let i = 0; i < flatShots.length; i++) {
    const current = flatShots[i]
    const next = flatShots[i + 1] // might be undefined

    if (current.isLastInScene) {
      // SCENE BOUNDARY or END OF VIDEO
      // Key: `${current.originalSceneId}-end`
      const endKey = `${current.originalSceneId}-end`
      const endTransition = getTransitionData(
        transitionsMap,
        endKey,
        DEFAULT_DURATION
      )

      if (endTransition) {
        if (next) {
          // Transition Between Scenes (Scene A -> Scene B)
          transitions.push({
            transition_type: 'between_scenes',
            from: current.sceneNumber,
            to: next.sceneNumber,
            transition_nature: endTransition.transition,
            scene_number: null,
            duration: Math.min(
              MAX_DURATION,
              Math.max(MIN_DURATION, endTransition.duration)
            )
          })
        } else {
          // End of Video (last shot -> null)
          transitions.push({
            transition_type: 'between_shots',
            from: current.shotIndexInScene, // Last shot index in this scene
            to: null,
            transition_nature: endTransition.transition,
            scene_number: current.sceneNumber,
            duration: Math.min(
              MAX_DURATION,
              Math.max(MIN_DURATION, endTransition.duration)
            )
          })
        }
      }
    } else {
      // WITHIN SCENE (Shot A -> Shot B in same scene)
      // Key for shot transition: `${current.originalSceneId}-${current.originalShotId}`
      const shotKey = `${current.originalSceneId}-${current.originalShotId}`
      const shotTransition = getTransitionData(
        transitionsMap,
        shotKey,
        DEFAULT_DURATION
      )

      if (shotTransition && next) {
        transitions.push({
          transition_type: 'between_shots',
          from: current.shotIndexInScene, // e.g., 1
          to: next.shotIndexInScene, // e.g., 2
          transition_nature: shotTransition.transition,
          scene_number: current.sceneNumber,
          duration: Math.min(
            MAX_DURATION,
            Math.max(MIN_DURATION, shotTransition.duration)
          )
        })
      }
    }
  }

  return transitions
}

/**
 * Constructs the payload for the assemble-scenes-with-audio API
 */
export const constructAssemblyPayload = (
  targetData,
  locationMap,
  isFinalVideo = false,
  transitionsMap = {}
) => {
  const normalizedScenes = normalizeScenes(targetData)
  const {
    clustered_shooting_list,
    flatShots,
    shot_volume_settings
  } = buildClusteredShootingList(normalizedScenes, locationMap)
  const voiceover_paths = buildVoiceoverPaths(normalizedScenes)
  const trimmed_video_paths = buildTrimmedVideoPaths(normalizedScenes)
  const transitions = buildTransitions(flatShots, transitionsMap)

  const scene_keys = Object.keys(clustered_shooting_list).filter(
    key => key !== 'location_map'
  )

  return {
    clustered_shooting_list,
    voiceover_paths,
    trimmed_video_paths,
    transitions,
    shot_volume_settings,
    scene_keys: isFinalVideo ? null : scene_keys,
    trim_mode: 'end'
  }
}

export const isDurationMismatch = (durationA, durationB) => {
  const a = parseFloat(durationA)
  const b = parseFloat(durationB)

  if (isNaN(a) || isNaN(b)) return false
  if (a === 0 || b === 0) return a !== b

  return Math.round(a) !== Math.round(b)
}
